import { Hotel, Loader2 } from 'lucide-react'
import React from 'react'
import { formatDateRange } from '@/lib/date'
import type { ToolRendererProps } from './types'
import { ToolDetailsPopover } from './ToolDetailsPopover'

/**
 * Hotel search result data structure (matches Go backend Result)
 */
interface HotelResult {
  summary: string
}

/**
 * Simple renderer for the "hotel_search" tool that displays
 * a concise summary of hotel search results.
 *
 * Example: "Hotels in Honolulu, Jan 15-18"
 */
export function HotelToolRenderer({
  toolCall,
  toolResponse,
  isCompleted,
  copyText,
}: ToolRendererProps) {
  // Show loading state if not completed
  if (!isCompleted || !toolResponse) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-magazine-pearl rounded-lg">
        <Loader2 className="w-3.5 h-3.5 text-magazine-dust animate-spin" />
        <span className="font-sans text-caption text-magazine-ash">
          Searching for hotels...
        </span>
      </div>
    )
  }

  // Handle errors
  if (toolResponse.error) {
    return (
      <div className="px-3 py-1.5 bg-magazine-coral/10 border border-magazine-coral/20 rounded-lg">
        <p className="font-sans text-caption text-magazine-coral">
          Error: {toolResponse.error}
        </p>
      </div>
    )
  }

  // Parse the tool response content
  let result: HotelResult
  try {
    result = JSON.parse(toolResponse.content || '{}')
  } catch (error) {
    console.error('Failed to parse hotel tool response:', error)
    return (
      <div className="px-3 py-1.5 bg-magazine-coral/10 border border-magazine-coral/20 rounded-lg">
        <p className="font-sans text-caption text-magazine-coral">
          Failed to parse hotel results
        </p>
      </div>
    )
  }

  // Parse arguments to extract location and dates for compact display
  let location = ''
  let checkInDate = ''
  let checkOutDate = ''
  try {
    const args = JSON.parse(toolCall.arguments || '{}')
    location = args.location || ''
    checkInDate = args.checkInDate || ''
    checkOutDate = args.checkOutDate || ''
  } catch (error) {
    // If we can't parse args, just show generic text
  }

  // Create compact hotel search display
  const searchDisplay = location
    ? `Hotels in ${location}${checkInDate ? ', ' + formatDateRange(checkInDate, checkOutDate) : ''}`
    : 'Hotel search'

  return (
    <ToolDetailsPopover
      toolCall={toolCall}
      toolResponse={toolResponse}
      copyText={copyText}
    >
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-magazine-lavender/10 border border-magazine-lavender/20 rounded-lg cursor-pointer hover:bg-magazine-lavender/15 transition-colors"
        title="Click to view details"
      >
        <Hotel className="w-3.5 h-3.5 text-magazine-lavender" />
        <span className="font-sans text-caption text-magazine-charcoal">
          {searchDisplay}
        </span>
      </div>
    </ToolDetailsPopover>
  )
}
